/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * UUID 를 생성한다.
 *
 * @file /src/utils/UUID.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 20.
 */
import { Format } from '@utils/Format';
export class UUID {
    static DNS_NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
    static URL_NAMESPACE = '6ba7b811-9dad-11d1-80b4-00c04fd430c8';
    static OID_NAMESPACE = '6ba7b812-9dad-11d1-80b4-00c04fd430c8';
    static X500_NAMESPACE = '6ba7b814-9dad-11d1-80b4-00c04fd430c8';

    private static readonly EPOCH_MS = 946684800000n; // 2000-01-01 UTC
    private static readonly NODE_ID = 0n; // 0~9
    private static readonly PID: bigint = BigInt(Math.floor(Math.random() * 64)); // 0~63
    private static lastMs = -1n;
    private static sequence = 0n;

    /**
     * UUID v1 를 생성한다.
     *
     * Version 1 UUID는 시간 기반이다. MAC 주소 또는 고유 문자열 기반의 노드 식별자를 받을 수 있다.
     *
     * @param node - 노드 식별자 (MAC 주소 또는 고유 문자열)
     * @returns UUID
     */
    static v1(node: string): string {
        const timestamp = Date.now();

        // 시간 기반 필드
        const timeLow = (timestamp >>> 0).toString(16).padStart(8, '0');
        const timeMid = ((timestamp / 0x100000000) >>> 0).toString(16).padStart(4, '0');
        const timeHiAndVersion = ((Math.floor(timestamp / 0x10000000000) & 0x0fff) | 0x1000)
            .toString(16)
            .padStart(4, '0');

        // clock sequence (랜덤)
        const clockSeq = (Math.floor(Math.random() * 0x3fff) | 0x8000).toString(16).padStart(4, '0');

        // SHA-1(node) → 앞 12자 (6바이트 → MAC 자리)
        const sha1Hex: string = Format.sha1(node);
        const nodeHex = sha1Hex.substring(0, 12);

        return `${timeLow}-${timeMid}-${timeHiAndVersion}-${clockSeq.substring(0, 2)}${clockSeq.substring(
            2,
            4
        )}-${nodeHex}`;
    }

    /**
     * UUID v3 를 생성한다.
     *
     * Version 3 UUID는 이름 기반이다. 네임스페이스(다른 유효한 UUID)와 이름이 필요하며,
     * 같은 네임스페이스와 이름이면 항상 동일한 값이 반환된다.
     *
     * @param namespace - 네임스페이스 UUID
     * @param name - 이름
     * @returns UUID
     */
    static v3(namespace: string, name: string): string {
        if (UUID.isValid(namespace) == false) {
            return '';
        }

        const nsHex = namespace.replace(/-/g, '');
        const nsBytes: number[] = [];
        for (let i = 0; i < 32; i += 2) {
            nsBytes.push(parseInt(nsHex.substring(i, i + 2), 16));
        }

        const fullBytes = nsBytes.concat([...new TextEncoder().encode(name)]);
        const hash = Format.md5(fullBytes.map((b) => String.fromCharCode(b)).join(''));

        return [
            hash.substring(0, 8),
            '-',
            hash.substring(8, 12),
            '-',
            ((parseInt(hash.substring(12, 16), 16) & 0x0fff) | 0x3000).toString(16).padStart(4, '0'),
            '-',
            ((parseInt(hash.substring(16, 20), 16) & 0x3fff) | 0x8000).toString(16).padStart(4, '0'),
            '-',
            hash.substring(20, 32),
        ].join('');
    }

    /**
     * UUID v4 를 생성한다.
     *
     * Version 4 UUID는 의사 난수 기반이다.
     *
     * @returns UUID
     */
    static v4(): string {
        // 최우선 → crypto.randomUUID()
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        // fallback → crypto.getRandomValues()
        if (
            typeof crypto !== 'undefined' &&
            typeof crypto.getRandomValues === 'function' &&
            typeof Uint8Array !== 'undefined'
        ) {
            const buffer = new Uint8Array(16);
            crypto.getRandomValues(buffer);

            buffer[6] = (buffer[6] & 0x0f) | 0x40; // version 4
            buffer[8] = (buffer[8] & 0x3f) | 0x80; // variant

            const byteToHex = (b: number) => b.toString(16).padStart(2, '0');

            return [
                byteToHex(buffer[0]),
                byteToHex(buffer[1]),
                byteToHex(buffer[2]),
                byteToHex(buffer[3]),
                '-',
                byteToHex(buffer[4]),
                byteToHex(buffer[5]),
                '-',
                byteToHex(buffer[6]),
                byteToHex(buffer[7]),
                '-',
                byteToHex(buffer[8]),
                byteToHex(buffer[9]),
                '-',
                byteToHex(buffer[10]),
                byteToHex(buffer[11]),
                byteToHex(buffer[12]),
                byteToHex(buffer[13]),
                byteToHex(buffer[14]),
                byteToHex(buffer[15]),
            ].join('');
        }

        // 최후 fallback → Math.random 기반
        const hex = [];

        const time = Date.now();
        const perf = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;

        for (let i = 0; i < 16; i++) {
            let rnd = Math.floor((Math.random() * 256) ^ (time >> (i % 6)) ^ (perf >> (i % 5)));
            hex.push(rnd & 0xff);
        }

        hex[6] = (hex[6] & 0x0f) | 0x40;
        hex[8] = (hex[8] & 0x3f) | 0x80;

        const byteToHex = (b: number) => b.toString(16).padStart(2, '0');

        return [
            byteToHex(hex[0]),
            byteToHex(hex[1]),
            byteToHex(hex[2]),
            byteToHex(hex[3]),
            '-',
            byteToHex(hex[4]),
            byteToHex(hex[5]),
            '-',
            byteToHex(hex[6]),
            byteToHex(hex[7]),
            '-',
            byteToHex(hex[8]),
            byteToHex(hex[9]),
            '-',
            byteToHex(hex[10]),
            byteToHex(hex[11]),
            byteToHex(hex[12]),
            byteToHex(hex[13]),
            byteToHex(hex[14]),
            byteToHex(hex[15]),
        ].join('');
    }

    /**
     * UUID v5 를 생성한다.
     *
     * Version 5 UUID는 이름 기반이다. 네임스페이스(다른 유효한 UUID)와 이름이 필요하며,
     * 같은 네임스페이스와 이름이면 항상 동일한 값이 반환된다.
     *
     * @param namespace - 네임스페이스 UUID
     * @param name - 이름
     * @returns UUID
     */
    static v5(namespace: string, name: string): string {
        const nsHex = namespace.replace(/-/g, '');
        const nsBytes: number[] = [];
        for (let i = 0; i < 32; i += 2) {
            nsBytes.push(parseInt(nsHex.substring(i, i + 2), 16));
        }

        const fullBytes = nsBytes.concat([...new TextEncoder().encode(name)]);
        const sha1Hex = Format.sha1(fullBytes.map((b) => String.fromCharCode(b)).join(''));

        const uuidHex = sha1Hex.substring(0, 32);

        return [
            uuidHex.substring(0, 8),
            '-',
            uuidHex.substring(8, 12),
            '-',
            ((parseInt(uuidHex.substring(12, 16), 16) & 0x0fff) | 0x5000).toString(16).padStart(4, '0'),
            '-',
            ((parseInt(uuidHex.substring(16, 20), 16) & 0x3fff) | 0x8000).toString(16).padStart(4, '0'),
            '-',
            uuidHex.substring(20, 32),
        ].join('');
    }

    /**
     * UUID v7 을 생성한다.
     *
     * Version 7 UUID는 시간 기반이며 정렬 가능하다. Unix 타임스탬프(밀리초)를 상위 비트에 사용하여
     * 단조 증가하는 값을 생성하므로 데이터베이스 기본 키로 적합하다.
     *
     * @param time - 초 단위 Unix 타임스탬프 (생략 시 현재 시각)
     * @returns UUID
     */
    static v7(time?: number): string {
        const ms = time !== undefined ? time * 1000 : Date.now();

        // 48비트 타임스탬프
        const timeHigh = Math.floor(ms / 0x100000000);
        const timeLow = ms >>> 0;
        const timeHex = timeHigh.toString(16).padStart(4, '0') + timeLow.toString(16).padStart(8, '0');

        // 12비트 랜덤 + version 7
        const randA = (Math.floor(Math.random() * 0x1000) | 0x7000).toString(16).padStart(4, '0');

        // 62비트 랜덤 + variant
        const randB = Math.floor(Math.random() * 0x40000000) + 0x80000000;
        const randC = Math.floor(Math.random() * 0x100000000)
            .toString(16)
            .padStart(8, '0');

        const randBHigh = (randB >>> 16).toString(16).padStart(4, '0');
        const randBLow = (randB & 0xffff).toString(16).padStart(4, '0');

        return `${timeHex.substring(0, 8)}-${timeHex.substring(8, 12)}-${randA}-${randBHigh}-${randBLow}${randC}`;
    }

    /**
     * UUID 버전을 가져온다.
     *
     * 주어진 UUID의 버전 번호(1, 3, 4, 5, 7 등)를 반환한다.
     * UUID가 유효하지 않으면 null을 반환한다.
     *
     * @param uuid - UUID 문자열
     * @returns UUID 버전 또는 null
     */
    static getVersion(uuid: string): number | null {
        if (UUID.isValid(uuid) == false) {
            return null;
        }

        const hex = uuid.replace(/-/g, '');
        return parseInt(hex[12], 16);
    }

    /**
     * UUID 유효성을 검사한다.
     *
     * 주어진 문자열이 하이픈 유무에 관계없이 표준 형식의 유효한 UUID인지 확인한다.
     *
     * @param uuid - UUID 문자열
     * @returns 유효 여부
     */
    static isValid(uuid: string): boolean {
        // 대시가 각 자리마다 개별적으로 optional(\-?)이면 그룹 경계가 어긋난 문자열도 총
        // 자릿수(32)만 맞으면 통과해버린다(실측 확인 — v7 의 dash 누락 버그가 이 검사를
        // 통과했었다) — 표준 하이픈 형식(8-4-4-4-12) 또는 하이픈 없는 32자 형식만 허용한다.
        return /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i.test(uuid);
    }

    /**
     * Snowflake 형태의 정수 UUID 를 생성한다.
     *
     * @param time - 초 단위 Unix 타임스탬프 (생략 시 현재 시각)
     * @returns 18자리 정수 문자열
     */
    static int(time?: number): string {
        const migration = time !== undefined;
        const ms = migration ? BigInt(time) * 1000n : BigInt(Date.now());

        if (ms < UUID.EPOCH_MS) {
            throw new Error('Timestamp is before UUID epoch.');
        }

        let sequence: bigint;

        if (migration == true) {
            sequence = BigInt(Math.floor(Math.random() * 128));
        } else {
            if (ms < UUID.lastMs) {
                throw new Error('Clock moved backwards.');
            }

            if (ms === UUID.lastMs) {
                UUID.sequence++;

                if (UUID.sequence > 127n) {
                    let currentMs: bigint;
                    do {
                        currentMs = BigInt(Date.now());
                    } while (currentMs <= UUID.lastMs);

                    UUID.sequence = 0n;
                    UUID.lastMs = currentMs;
                }
            } else {
                UUID.sequence = 0n;
            }

            UUID.lastMs = ms;
            sequence = UUID.sequence;
        }

        const timestamp = ms - UUID.EPOCH_MS;
        const worker = UUID.NODE_ID * 64n + UUID.PID;
        const id = 100000000000000000n + (timestamp << 17n) + (worker << 7n) + sequence;

        if (id > 999999999999999999n) {
            throw new Error('UUID overflow: CHAR(18) limit exceeded.');
        }

        return id.toString();
    }
}
